import { WarningTwoIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext } from '@hb/react'
import { UserGroup } from '@hb/shared'
import { useCallback, useContext, useState } from 'react'
import { updateAssessmentMiscarriedStatus } from '../../backend/functions/other'
import { useProfile } from '../../contexts/user/ProfileContext'
import { MiscarriedHistoryPopover } from '../Users/Profile/ProfileMiscarried'
import { AddAlertModal } from './AddAlertModal'

const AddAlertButton = ({ onClick }: { onClick?: () => void }) => (
  <Button
    // variant="unstyled"
    variant="outline"
    size="xs"
    onClick={onClick}
    bg="white"
    colorScheme="red"
    fontSize="xs">
    + ADD ALERT
  </Button>
)

export const AddAlertPopover = ({
  accessLevel,
  type,
  inList,
  placement = 'bottom',
}: {
  accessLevel?: UserGroup
  type?: 'assessment' | 'patient'
  inList?: boolean
  placement?: PopoverProps['placement']
}) => {
  const [addingAlert, setAddingAlert] = useState(false)
  const { isMobile } = useContext(ScreenContext)
  const {
    selectedAssessment: { populated: selectedAssessment },
    assessmentId,
  } = useProfile()
  const { miscarried, miscarriedHistory } = selectedAssessment ?? {}
  const [reportingMiscarried, setReportingMiscarried] = useState(false)
  const { processResponse } = useContext(PopUpMessageContext)
  const handleReportMiscarried = useCallback(async () => {
    if (!assessmentId) {
      processResponse({ error: 'No pregnancy ID' })
      return
    }
    setReportingMiscarried(true)
    try {
      await updateAssessmentMiscarriedStatus({
        assessmentId,
        miscarried: true,
      })
    } catch (e: any) {
      processResponse({
        error: e?.message || 'Error updating miscarried status',
      })
    }
    setReportingMiscarried(false)
  }, [assessmentId, processResponse])

  const body =
    assessmentId && type !== 'patient' && !miscarried ? (
      <Popover trigger="hover" closeOnBlur={false} strategy="fixed" isLazy placement={placement}>
        <PopoverTrigger>
          <Flex>
            {inList ? (
              <AddAlertButton />
            ) : (
              <Button
                aria-label="Add Alert"
                variant="ghost"
                gap={1}
                color="blackAlpha.600"
                size="sm"
                _hover={{
                  bg: 'blackAlpha.200',
                }}>
                <WarningTwoIcon w={4} h={4} />
                {isMobile ? <Text>No alerts</Text> : null}
              </Button>
            )}
          </Flex>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="220px" p={0} boxShadow="1px 1px 3px #00000077">
            <PopoverArrow />
            <PopoverBody borderRadius={6} overflow="hidden" bg="gray.50">
              <VStack spacing={2}>
                <Button
                  w="100%"
                  onClick={() => setAddingAlert(true)}
                  color="red.600"
                  bg="white"
                  size="sm"
                  borderColor="red.600"
                  borderWidth="1px">
                  ADD
                  {type ? ` ${type === 'assessment' ? 'PREGNANCY' : 'PATIENT'}` : ''} ALERT
                </Button>
                <Flex w="100%" gap={2} align="center">
                  <Button
                    flex={1}
                    minW="0"
                    onClick={handleReportMiscarried}
                    isLoading={reportingMiscarried}
                    bg="red.600"
                    size="sm"
                    color="white">
                    REPORT MISCARRIED
                  </Button>
                  {miscarriedHistory?.length ? (
                    <MiscarriedHistoryPopover assessment={selectedAssessment} />
                  ) : null}
                </Flex>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    ) : (
      <AddAlertButton onClick={() => setAddingAlert(true)} />
    )

  return (
    <Flex>
      {body}
      {addingAlert ? (
        <AddAlertModal
          type={type}
          accessLevel={accessLevel}
          onClose={() => setAddingAlert(false)}
        />
      ) : null}
    </Flex>
  )
}
